<template>
  <div class="new-shipment-address-not-found-dialog-map">
    <div
      ref="container"
      class="new-shipment-address-not-found-dialog-map__map"
      data-test="map"
    />
  </div>
</template>

<script>
  import { fitToPositions, icons } from '@/services/GoogleMaps'
  import useMap from '@/composables/useMap'

  /**
   * @typedef Marker
   * @type {object}
   * @property {string} id
   * @property {boolean} active - Whether the result is selected or hovered
   * @property {object} location
   * @property {number} location.lat
   * @property {number} location.lng
   * @property {any} component
   */

  /**
   * @module component - NewShipmentAddressNotFoundDialogMap
   * @param {string} [selectedResult=null] - The selected result id
   * @param {Array<Marker>} [markers=[]] - List of markers
   */
  export default {
    name: 'NewShipmentAddressNotFoundDialogMap',
    props: {
      selectedResult: {
        type: String,
        default: null
      },
      markers: {
        type: Array,
        default: () => ([])
      }
    },
    setup () {
      const { createMap, mapData, map } = useMap()

      return {
        createMap,
        mapData,
        map
      }
    },
    data () {
      return {
        /**
         * The markers currently on the map, by result id, so a redraw can take
         * the previous ones off.
         * @type {Array<any>}
         */
        mapMarkers: []
      }
    },
    watch: {
      markers: {
        handler () {
          this.drawMarkers()
        },
        deep: true
      }
    },
    async mounted () {
      await this.createMap(this.$refs.container, {
        zoom: 2,
        scrollwheel: false
      })

      this.drawMarkers()
    },
    beforeDestroy () {
      this.removeMarkers()
    },
    methods: {
      /**
       * Draws one pin per result, lit for the selected or hovered one.
       * @function drawMarkers
       */
      drawMarkers () {
        if (!this.map) return

        this.removeMarkers()

        this.mapMarkers = this.markers.map(marker => {
          const mapMarker = new window.google.maps.Marker({
            map: this.map,
            position: marker.location,
            icon: marker.active ? icons.pickup('in_progress') : icons.disabled()
          })

          mapMarker.addListener('click', () => this.select(marker))
          mapMarker.addListener('mouseover', () => this.$emit('mouseover-result', marker))
          mapMarker.addListener('mouseout', () => this.$emit('mouseout-result', marker))

          return mapMarker
        })
      },
      /**
       * @function removeMarkers
       */
      removeMarkers () {
        this.mapMarkers.forEach(marker => marker.setMap(null))
        this.mapMarkers = []
      },
      /**
       * @function select
       * @param {Object} marker
       */
      select (marker) {
        this.$emit('select-result', marker)
      },
      /**
       * Fly the map to the current markers displayed in the map
       * @function flyToMarkers
       */
      flyToMarkers () {
        if (!this.map || !this.mapMarkers.length) return

        fitToPositions(this.map, this.mapMarkers.map(marker => marker.getPosition()))
      }
    }
  }
</script>

<style lang="scss" scoped>
  .new-shipment-address-not-found-dialog-map {
    &,
    &__map {
      border-radius: 0 0.25rem 0.25rem 0;
    }

    &-container,
    &__map {
      z-index: 0;
      width: 100%;
      height: 360px;
      max-height: 100vh;
      overflow: hidden;
    }
  }
</style>
